import React, { useState, useEffect } from "react";
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Space,
  Image,
  Badge,
  Typography,
  Collapse,
} from "antd";
import {
  ArrowLeftOutlined,
  EyeOutlined,
  EditOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  PlayCircleOutlined,
  FileTextOutlined,
  TagOutlined,
  UserOutlined,
  HourglassOutlined,
  BookOutlined,
  BulbOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useDynamicApi } from "../../hooks/useApi";
import { API_ENDPOINTS } from "../../config/api";
import Toast from "../../utils/toast";
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;
const { Panel } = Collapse;

const ExerciseDetails = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [exercise, setExercise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [themeOptions, setThemeOptions] = useState([]);
  const [parentExercise, setParentExercise] = useState(null);

  // API hooks
  const { execute: getExercise, loading: exerciseLoading } =
    useDynamicApi("GET");
  const { execute: fetchThemes } = useDynamicApi("GET");

  // Constants
  const AGE_GROUP_CHOICES = [
    { value: "6-9", label: t("exerciseManager.ageGroup6_9") || "6-9 years" },
    {
      value: "10-14",
      label: t("exerciseManager.ageGroup10_14") || "10-14 years",
    },
    {
      value: "15-18",
      label: t("exerciseManager.ageGroup15_18") || "15-18 years",
    },
  ];

  const ACTION_CHOICES = [
    {
      value: "MOBILITY_STABILITY",
      label:
        t("exerciseManager.actionMobilityStability") || "Mobility & Stability",
    },
    {
      value: "COORDINATION",
      label: t("exerciseManager.actionCoordination") || "Coordination",
    },
    {
      value: "POWER_ACTIVATION",
      label: t("exerciseManager.actionPowerActivation") || "Power Activation",
    },
    { value: "CARDIO", label: t("exerciseManager.actionCardio") || "Cardio" },
    {
      value: "SPORT_SPECIFIC",
      label: t("exerciseManager.actionSportSpecific") || "Sport Specific",
    },
    {
      value: "GROUP_DYNAMICS",
      label: t("exerciseManager.actionGroupDynamics") || "Group Dynamics",
    },
  ];

  const STATUS_CHOICES = [
    { value: "ACTIVE", label: t("common.active") },
    { value: "INACTIVE", label: t("common.inactive") },
  ];

  // Load themes for displaying theme name
  const loadThemes = async () => {
    try {
      const response = await fetchThemes(
        API_ENDPOINTS.THEMES_MANAGER_LIST_WITHOUT_PAGINATE,
      );
      if (response?.data && Array.isArray(response.data)) {
        const options = response.data.map((theme) => ({
          value: theme.id.toString(),
          label: `${theme.theme_name} - ${theme.component} (${theme.age_group})`,
        }));
        setThemeOptions(options);
      }
    } catch (error) {
      console.error("Error loading themes:", error);
    }
  };

  // Load exercise details
  const loadExerciseDetails = async () => {
    setLoading(true);
    try {
      const endpoint = `${API_ENDPOINTS.EXERCISE_UPDATE}/${id}`;
      const response = await getExercise(endpoint);

      if (response?.status === true && response.data) {
        setExercise(response.data);

        // If it's a variation, load parent exercise
        if (response.data.is_variation && response.data.parent_exercise) {
          loadParentExercise(response.data.parent_exercise);
        }
      } else {
        Toast.error(t("exerciseManager.failedFetchDetails"));
        navigate("/exercise");
      }
    } catch (error) {
      console.error("Error fetching exercise details:", error);
      Toast.error(error.message || t("exerciseManager.failedFetchDetails"));
      navigate("/exercise");
    } finally {
      setLoading(false);
    }
  };

  // Load parent exercise if variation
  const loadParentExercise = async (parentId) => {
    try {
      const endpoint = `${API_ENDPOINTS.EXERCISE_UPDATE}/${parentId}`;
      const response = await getExercise(endpoint);
      if (response?.status === true && response.data) {
        setParentExercise(response.data);
      }
    } catch (error) {
      console.error("Error loading parent exercise:", error);
    }
  };

  useEffect(() => {
    if (id) {
      loadThemes();
      loadExerciseDetails();
    }
  }, [id]);

  // Helper functions
  const getChoiceLabel = (value, choices) => {
    const choice = choices.find((c) => c.value === value);
    return choice ? choice.label : value || "N/A";
  };

  const getThemeName = (themeId) => {
    if (!themeId) return "N/A";
    const theme = themeOptions.find((t) => t.value === themeId.toString());
    return theme ? theme.label : themeId;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === "nl" ? "nl-NL" : "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading || exerciseLoading) return null;

  if (!exercise) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Title level={3}>{t("exerciseManager.noExercisesFound")}</Title>
          <Button type="primary" onClick={() => navigate("/exercise")}>
            {t("common.previous")} {t("exerciseManager.exercises")}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="exercise-details-page">
      {/* Header with back button and actions */}
      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/exercise")}
          >
            {t("common.previous")} {t("exerciseManager.exercises")}
          </Button>
          <Title level={4} style={{ margin: 0 }}>
            {t("exerciseManager.details")}
          </Title>
        </Space>
        {/* Edit button commented out like in UserDetails */}
        {/* <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => navigate(`/exercises/edit/${exercise.id}`)}
        >
          Edit Exercise
        </Button> */}
      </div>

      {/* Main Content */}
      <Card>
        {/* Avatar and Title Section */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "24px",
          }}
        >
          <div>
            <h1 style={{ margin: 0, marginBottom: "8px" }}>{exercise.title}</h1>
            <Space size="middle">
              {exercise.is_variation && (
                <Tag color="blue">{t("exerciseManager.variation")}</Tag>
              )}
              <Badge
                status={exercise.status === "ACTIVE" ? "success" : "default"}
                text={
                  exercise.status === "ACTIVE"
                    ? t("common.active")
                    : t("common.inactive")
                }
              />
            </Space>
          </div>
        </div>

        {/* Details in Descriptions */}
        <Descriptions bordered column={2}>
          <Descriptions.Item label={t("exerciseManager.title")} span={2}>
            <Space>{exercise.title}</Space>
          </Descriptions.Item>

          <Descriptions.Item label={t("exerciseManager.ageGroup")} span={1}>
            {getChoiceLabel(exercise.age_group, AGE_GROUP_CHOICES)}
          </Descriptions.Item>

          <Descriptions.Item label={t("exerciseManager.theme")} span={2}>
            <Space>{getThemeName(exercise.theme_manager)}</Space>
          </Descriptions.Item>

          <Descriptions.Item label={t("exerciseManager.assignment")} span={2}>
            <div
              style={{
                whiteSpace: "pre-wrap",
                backgroundColor: "#f5f5f5",
                padding: "12px",
                borderRadius: "4px",
              }}
            >
              {exercise.assignment_task}
            </div>
          </Descriptions.Item>

          {exercise.video_url && (
            <Descriptions.Item label={t("exerciseManager.video")} span={2}>
              <a
                href={exercise.video_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#1890ff" }}
              >
                <PlayCircleOutlined /> {t("exerciseManager.watchVideo")}
              </a>
            </Descriptions.Item>
          )}

          <Descriptions.Item label={t("exerciseManager.thumbnail")} span={2}>
            {exercise.thumbnail_url ? (
              <Image src={exercise.thumbnail_url} width={200} />
            ) : (
              "N/A"
            )}
          </Descriptions.Item>

          <Descriptions.Item label={t("common.status")} span={1}>
            <Tag color={exercise.status === "ACTIVE" ? "green" : "default"}>
              {exercise.status === "ACTIVE"
                ? t("common.active")
                : t("common.inactive")}
            </Tag>
          </Descriptions.Item>

          <Descriptions.Item label={t("exerciseManager.isVariation")} span={1}>
            {exercise.is_variation ? t("common.yes") : t("common.no")}
          </Descriptions.Item>

          {exercise.is_variation && (
            <Descriptions.Item
              label={t("exerciseManager.variationTitle")}
              span={1}
            >
              {exercise.variation_title || "N/A"}
            </Descriptions.Item>
          )}

          <Descriptions.Item label={t("common.createdAt")} span={1}>
            <Space>
              <CalendarOutlined />
              {formatDate(exercise.created_at)}
            </Space>
          </Descriptions.Item>

          <Descriptions.Item label={t("common.lastUpdated")} span={1}>
            <Space>
              <ClockCircleOutlined />
              {formatDate(exercise.updated_at)}
            </Space>
          </Descriptions.Item>
        </Descriptions>

        {/* Parent Exercise Info for Variations */}
        {exercise.is_variation && parentExercise && (
          <div
            style={{
              marginTop: "20px",
              padding: "15px",
              backgroundColor: "#e6f7ff",
              border: "1px solid #91d5ff",
              borderRadius: "6px",
            }}
          >
            <div
              style={{
                display: "flex",
                marginBottom: "8px",
                alignItems: "center",
                gap: "16px",
              }}
            >
              <strong>{t("exerciseManager.parentExercise")}:</strong>
              <span>{parentExercise.title}</span>
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() =>
                  navigate(`/exercises/details/${parentExercise.id}`)
                }
              >
                {t("exerciseManager.viewParent")}
              </Button>
            </div>
          </div>
        )}

        {/* ================= LEVELS SECTION ================= */}
        {exercise.levels && exercise.levels.length > 0 && (
          <div style={{ marginTop: "24px" }}>
            <Title level={4} style={{ marginBottom: "16px" }}>
              {t("exerciseManager.levelsTitle")}
            </Title>
            <Collapse defaultActiveKey={["BASIC"]}>
              {exercise.levels.map((level) => (
                <Panel
                  header={
                    <Space>
                      <Tag color="blue">{level.level}</Tag>
                      <Text>
                        {level.players_count || 0}{" "}
                        {t("exerciseManager.players")} ·{" "}
                        {level.duration_minutes || 0}{" "}
                        {t("exerciseManager.minutes")}
                      </Text>
                    </Space>
                  }
                  key={level.level}
                >
                  <Descriptions column={1} bordered size="small">
                    <Descriptions.Item
                      label={t("exerciseManager.playersCount")}
                    >
                      {level.players_count ?? "—"}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("exerciseManager.durationMinutes")}
                    >
                      {level.duration_minutes ?? "—"}{" "}
                      {t("exerciseManager.minutes")}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("exerciseManager.rules")}>
                      {level.rules && level.rules.length > 0 ? (
                        <ul style={{ margin: 0, paddingLeft: "20px" }}>
                          {level.rules.map((rule, idx) => (
                            <li key={idx}>{rule}</li>
                          ))}
                        </ul>
                      ) : (
                        "—"
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("exerciseManager.practicePoints")}
                    >
                      {level.practice_points &&
                      level.practice_points.length > 0 ? (
                        <ul style={{ margin: 0, paddingLeft: "20px" }}>
                          {level.practice_points.map((point, idx) => (
                            <li key={idx}>{point}</li>
                          ))}
                        </ul>
                      ) : (
                        "—"
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={t("exerciseManager.coachingTips")}
                    >
                      {level.coaching_tips && level.coaching_tips.length > 0 ? (
                        <ul style={{ margin: 0, paddingLeft: "20px" }}>
                          {level.coaching_tips.map((tip, idx) => (
                            <li key={idx}>{tip}</li>
                          ))}
                        </ul>
                      ) : (
                        "—"
                      )}
                    </Descriptions.Item>
                  </Descriptions>
                </Panel>
              ))}
            </Collapse>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ExerciseDetails;
